import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../ui.jsx';
import { OrDivider } from './AuthLayout.jsx';

// "Sign in with Google" via Google Identity Services. The button itself is
// rendered by Google (google.accounts.id.renderButton), so it follows
// Google's branding rules exactly. Google returns a signed ID token, which
// the server verifies (POST /api/auth/google) before signing anyone in.
// Nothing is shown until GOOGLE_CLIENT_ID is configured on the server.

let configPromise = null;
const getConfig = () => (configPromise ||= api('/auth/config').catch(() => ({ googleClientId: null })));

// The button's language comes from the script's ?hl= parameter (Google
// ignores the renderButton `locale` option), so the script is loaded per
// language and reloaded when the visitor switches FR/EN.
let script = { lang: null, promise: null };
function loadGis(lang) {
  if (script.lang === lang && script.promise) return script.promise;
  document.querySelectorAll('script[data-gis]').forEach((el) => el.remove());
  if (window.google?.accounts) delete window.google.accounts;
  script = {
    lang,
    promise: new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://accounts.google.com/gsi/client?hl=${lang}`;
      s.async = true;
      s.dataset.gis = lang;
      s.onload = resolve;
      s.onerror = () => { script = { lang: null, promise: null }; reject(new Error('gis_load_failed')); };
      document.head.appendChild(s);
    }),
  };
  return script.promise;
}

// extra: more fields for /auth/google (e.g. { invite } from an invite link).
export default function GoogleButton({ text = 'signin_with', divider, onSignedIn, extra }) {
  const { lang, errorText } = useI18n();
  const box = useRef(null);
  const [clientId, setClientId] = useState(undefined);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const handler = useRef(onSignedIn);
  handler.current = onSignedIn;
  const extraRef = useRef(extra);
  extraRef.current = extra;

  useEffect(() => {
    let alive = true;
    getConfig().then((c) => alive && setClientId(c.googleClientId || null));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!clientId || !box.current) return undefined;
    let alive = true;
    loadGis(lang === 'en' ? 'en' : 'fr').then(() => {
      if (!alive || !box.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: async ({ credential }) => {
          setError(''); setBusy(true);
          try {
            const result = await api('/auth/google', { method: 'POST', body: { ...(extraRef.current || {}), credential, lang } });
            handler.current(result);
          } catch (err) {
            setError(errorText(err));
          } finally {
            setBusy(false);
          }
        },
      });
      box.current.innerHTML = '';
      window.google.accounts.id.renderButton(box.current, {
        type: 'standard', theme: 'outline', size: 'large', shape: 'rectangular',
        text, logo_alignment: 'center',
        width: Math.min(400, Math.max(240, box.current.parentElement.clientWidth)),
      });
    }).catch(() => alive && setError(errorText({ code: 'google_unavailable' })));
    return () => { alive = false; };
  }, [clientId, lang, text, errorText]);

  // Not configured on the server: no button and no "or" divider.
  if (clientId === null) return null;
  return (
    <>
      {divider === 'before' && <OrDivider />}
      <div className="google-signin" aria-busy={busy}>
        <div ref={box} className="google-signin-button" />
        <Alert>{error}</Alert>
      </div>
      {divider === 'after' && <OrDivider />}
    </>
  );
}
